/**
 * PSTUDIO-1433: columns whose value can be JSON (or "mixed" — JSON or a
 * scalar). The Expanded Cell View panel edits these with a CodeMirror JSON
 * editor; every other text column keeps the plain <textarea>. This is a
 * COLUMN-level capability — it does NOT depend on the current value or the
 * row's asset type, so the editor never flips mid-edit (contrast the
 * value-dependent tableStore.isJsonColumn, which is right for canonical
 * (de)serialization but wrong for choosing the editor).
 *
 * `analyticsEvent` is in the set for the PANEL's editor only. Its grid cell is a
 * select column (PSTUDIO-4784, `SELECT_COLUMNS`), so the grid-side consequences
 * of being JSON-capable — auto-opening the panel on an edit-entry gesture,
 * parking a typed character for the panel — do not reach it.
 *
 * Note: NodeColumns is imported TYPE-ONLY (`import type`) so it is erased at
 * runtime and cannot create a value-level circular dependency (table-store.ts
 * imports from ./utils, this directory). The `satisfies NodeColumns[]` below is
 * a compile-time guard: if any of these column keys is ever renamed in the
 * NodeColumns enum, this stops compiling instead of silently returning the wrong
 * editor (a silent regression the old hand-verified string literals allowed).
 */
import type { NodeColumns } from '../table-store';

const JSON_CAPABLE_COLUMNS = new Set<string>([
  'parameterInput',
  'analyticsEvent',
  'richAssetContent',
  // `${NodeColumns}` is the union of the enum's string VALUES (a template-literal
  // type over the string enum), so this compile-time guard accepts a literal only
  // if it is still a real NodeColumns value — renaming the enum breaks the build.
] satisfies `${NodeColumns}`[]);

export function isJsonCapableColumn(columnId: string | undefined): boolean {
  return !!columnId && JSON_CAPABLE_COLUMNS.has(columnId);
}
